import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { toastChannel } from './feedbackStore';
import { tokens as c } from '../../theme/tokens';

const DEFAULT_MS = { success: 3000, info: 4000, warning: 5000, error: 5000 };

const ICONS = {
  success: { Icon: CheckRoundedIcon, color: c.SUCCESS_TEXT },
  error: { Icon: PriorityHighRoundedIcon, color: c.DANGER_TEXT },
  warning: { Icon: WarningAmberRoundedIcon, color: c.WARNING_TEXT },
  info: { Icon: InfoOutlinedIcon, color: c.INFO_TEXT },
};

const durationOf = (toast) => {
  if (toast.duration === null || toast.duration === 0) return null;
  const base = typeof toast.duration === 'number' ? toast.duration : DEFAULT_MS[toast.kind] || 4000;
  return toast.actionLabel ? Math.max(base, 6000) : base;
};

// The queue lives in one reducer, so toasts sent in the same tick (or
// before the host mounted) are all shown, in order, and none is lost.
const initialState = { queue: [], current: null, open: false };

function reducer(state, action) {
  switch (action.type) {
    case 'show': {
      const { toast } = action;
      if (state.current && state.current.id === toast.id) return { ...state, current: toast, open: true }; // same id: update in place
      return { ...state, queue: state.queue.filter((q) => q.id !== toast.id).concat([toast]) };
    }
    case 'dismiss': {
      const queue = state.queue.filter((q) => q.id !== action.id);
      if (state.current && state.current.id === action.id) return { ...state, queue, open: false };
      return queue.length === state.queue.length ? state : { ...state, queue };
    }
    case 'close':
      return state.open ? { ...state, open: false } : state;
    case 'next': {
      if (state.current || state.queue.length === 0) return state;
      const [next, ...rest] = state.queue;
      return { queue: rest, current: next, open: true };
    }
    case 'exited':
      return state.current && state.current.id === action.id ? { ...state, current: null, open: false } : state;
    default:
      return state;
  }
}

// One toast at a time, bottom centre; later ones wait their turn. Dark ink
// card, an icon on a white circle, 15-px words, an optional action.
export default function ToastHost() {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { queue, current, open } = state;

  useEffect(
    () =>
      toastChannel.subscribe((evt) => {
        if (evt.type === 'show') dispatch({ type: 'show', toast: evt.toast });
        else if (evt.type === 'dismiss') dispatch({ type: 'dismiss', id: evt.id });
      }),
    []
  );

  // Show the next one when nothing is on screen; when more are waiting, let
  // the one on screen leave first.
  useEffect(() => {
    if (queue.length === 0) return;
    if (!current) dispatch({ type: 'next' });
    else if (open) dispatch({ type: 'close' });
  }, [queue, current, open]);

  // A toast closed before it ever showed gets no exit animation; move on anyway.
  useEffect(() => {
    if (!current || open) return undefined;
    const id = current.id;
    const timer = setTimeout(() => dispatch({ type: 'exited', id }), 1000);
    return () => clearTimeout(timer);
  }, [current, open]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return; // a click elsewhere never hides news
    dispatch({ type: 'close' });
  };

  if (!current) return null;
  const handleExited = () => dispatch({ type: 'exited', id: current.id });
  const { Icon, color } = ICONS[current.kind] || ICONS.info;
  const persistent = durationOf(current) === null;

  return (
    <Snackbar
      key={current.id}
      open={open}
      autoHideDuration={durationOf(current)}
      onClose={handleClose}
      TransitionProps={{ onExited: handleExited }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 16, sm: 24 } }}
    >
      <Box
        role={current.kind === 'error' ? 'alert' : 'status'}
        aria-live={current.kind === 'error' ? 'assertive' : 'polite'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 48,
          width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
          minWidth: { sm: 320 },
          maxWidth: 560,
          boxSizing: 'border-box',
          px: 2,
          py: 1.25,
          borderRadius: '12px',
          bgcolor: c.INK,
          color: c.WHITE,
          boxShadow: '0 8px 24px rgba(15,23,42,0.28)',
        }}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: c.WHITE,
            color,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Icon sx={{ fontSize: 17 }} />
        </Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 15, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          {current.message}
        </Box>
        {current.actionLabel && typeof current.onAction === 'function' ? (
          <Button
            onClick={() => {
              dispatch({ type: 'close' });
              current.onAction();
            }}
            sx={{
              color: c.WHITE,
              fontWeight: 700,
              flex: 'none',
              minHeight: 36,
              px: 1.25,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
              '@media (pointer: coarse)': { minHeight: 44 },
            }}
          >
            {current.actionLabel}
          </Button>
        ) : null}
        {persistent ? (
          <IconButton
            size="small"
            aria-label={t('close_message', { defaultValue: 'Close message' })}
            onClick={() => dispatch({ type: 'close' })}
            sx={{ color: c.WHITE, flex: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
    </Snackbar>
  );
}
